<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import type { DashboardSummary, DashboardStation } from '@/types/station'
import AlarmDonutChart from './AlarmDonutChart.vue'
import { useI18n } from '@/i18n'

const props = defineProps<{
  summary: DashboardSummary
  lastUpdated?: Date | null
  warningStations?: DashboardStation[]
}>()
const { locale, t } = useI18n()

// Get the latest warning station (first one in the list)
const latestWarning = computed(() => {
  if (!props.warningStations || props.warningStations.length === 0) return null
  return props.warningStations[0]
})

// Format relative time for latest warning
const latestWarningTime = computed(() => {
  if (!latestWarning.value?.last_reading) return null
  const date = new Date(latestWarning.value.last_reading)
  const now = new Date()
  const diff = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diff < 60) return t('Just now')
  if (diff < 3600) return t('{count} min ago', { count: Math.floor(diff / 60) })
  if (diff < 86400) return t('{count} hr ago', { count: Math.floor(diff / 3600) })
  return date.toLocaleDateString(locale.value === 'th' ? 'th-TH' : 'en-GB', { day: 'numeric', month: 'short' })
})

// สัดส่วนสถานีที่ออนไลน์ — active_stations นับ "ที่ยังส่งข้อมูลเข้ามา" ล้วนๆ
// ไม่หัก alert/warning ออก แถบนี้จึงบอกความสมบูรณ์ของการเชื่อมต่อ ไม่ใช่
// สถานะการแจ้งเตือน (ดู alert/warning ที่การ์ดถัดไปแทน)
const healthPercent = computed(() => {
  if (props.summary.total_stations === 0) return 100
  return Math.round((props.summary.active_stations / props.summary.total_stations) * 100)
})

// Progress bar color based on health
const progressBarColor = computed(() => {
  if (healthPercent.value >= 90) return 'bg-green-500'
  if (healthPercent.value >= 70) return 'bg-yellow-500'
  return 'bg-red-500'
})

// Warning card background color
const warningCardClass = computed(() => {
  if (props.summary.warning_stations === 0) return ''
  if (props.summary.warning_stations <= 2) return 'bg-yellow-50 border-yellow-200'
  return 'bg-red-50 border-red-200'
})

// Format last updated time
const lastUpdatedText = computed(() => {
  if (!props.lastUpdated) return null
  const now = new Date()
  const diff = Math.floor((now.getTime() - props.lastUpdated.getTime()) / 1000)

  if (diff < 60) return t('{count} sec ago', { count: diff })
  if (diff < 3600) return t('{count} min ago', { count: Math.floor(diff / 60) })
  return props.lastUpdated.toLocaleTimeString(locale.value === 'th' ? 'th-TH' : 'en-GB', { hour: '2-digit', minute: '2-digit' })
})
</script>

<template>
  <div class="space-y-2 mb-6">
    <!-- Cards Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <!-- Total Power & Energy -->
      <div class="summary-card">
        <div class="flex items-center justify-between mb-3">
          <p class="text-xs text-gray-400 uppercase tracking-wider font-semibold">{{ t('Total Power') }}</p>
          <div class="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
        </div>
        <p class="text-4xl font-bold text-gray-800 mb-1">
          {{ Math.round(summary.total_power_kw).toLocaleString() }}
          <span class="text-xl font-normal text-gray-400 ml-1">kW</span>
        </p>
        <!-- Energy Today -->
        <div class="mt-auto pt-3 border-t border-gray-100">
          <div class="flex justify-between items-center">
            <span class="text-xs text-gray-400">{{ t('Energy today') }}</span>
            <span class="text-sm font-semibold text-gray-700">
              {{ Math.round(summary.total_energy_kwh).toLocaleString() }} kWh
            </span>
          </div>
        </div>
      </div>

      <!-- Active Stations -->
      <div class="summary-card">
        <div class="flex items-center justify-between mb-3">
          <p class="text-xs text-gray-400 uppercase tracking-wider font-semibold">{{ t('Active Stations') }}</p>
          <div class="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
        </div>
        <p class="text-4xl font-bold text-gray-800 mb-1">
          {{ summary.active_stations }}
          <span class="text-xl font-normal text-gray-400 ml-1">/ {{ summary.total_stations }}</span>
        </p>
        <!-- Progress Bar -->
        <div class="mt-auto pt-3 border-t border-gray-100">
          <div class="flex justify-between items-center mb-1">
            <span class="text-xs text-gray-400">{{ t('Online') }}</span>
            <span class="text-xs font-semibold" :class="healthPercent >= 90 ? 'text-green-600' : healthPercent >= 70 ? 'text-yellow-600' : 'text-red-600'">
              {{ healthPercent }}%
            </span>
          </div>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="h-2 rounded-full transition-all duration-300"
              :class="progressBarColor"
              :style="{ width: healthPercent + '%' }"
            ></div>
          </div>
        </div>
      </div>

      <!-- Protection Alarms -->
      <RouterLink to="/alarms" class="summary-card block cursor-pointer">
        <div class="flex items-center justify-between mb-3">
          <p class="text-xs text-gray-400 uppercase tracking-wider font-semibold">{{ t('Protection Alarms') }}</p>
          <div class="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        </div>

        <!-- Donut Chart -->
        <div class="flex-1 flex items-center justify-center py-2">
          <AlarmDonutChart :alarms="summary.alarms_by_type" />
        </div>

        <!-- Legend -->
        <div class="mt-auto pt-3 border-t border-gray-100">
          <div class="flex justify-center gap-3 text-xs">
            <span class="flex items-center">
              <span class="w-2 h-2 rounded-full bg-red-500 mr-1"></span>
              GFR
            </span>
            <span class="flex items-center">
              <span class="w-2 h-2 rounded-full bg-orange-500 mr-1"></span>
              OVPR
            </span>
            <span class="flex items-center">
              <span class="w-2 h-2 rounded-full bg-yellow-500 mr-1"></span>
              UVPR
            </span>
          </div>
        </div>
      </RouterLink>

      <!-- Warnings -->
      <RouterLink to="/alarms?tab=warnings" class="summary-card block cursor-pointer" :class="warningCardClass">
        <div class="flex items-center justify-between mb-3">
          <p class="text-xs text-gray-400 uppercase tracking-wider font-semibold">{{ t('Warnings') }}</p>
          <div class="w-10 h-10 rounded-lg" :class="summary.warning_stations > 0 ? 'bg-yellow-100' : 'bg-green-100'" >
            <div class="w-full h-full flex items-center justify-center">
              <svg v-if="summary.warning_stations > 0" class="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <svg v-else class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        <!-- Warning Count -->
        <div class="flex items-center mb-3">
          <div class="flex items-center justify-center w-14 h-14 rounded-full" :class="summary.warning_stations > 0 ? 'bg-yellow-100' : 'bg-green-100'">
            <span class="text-2xl font-bold" :class="summary.warning_stations > 0 ? 'text-yellow-600' : 'text-green-600'">
              {{ summary.warning_stations }}
            </span>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-gray-700">
              {{ summary.warning_stations > 0 ? t('Stations') : t('All Clear') }}
            </p>
            <p class="text-xs text-gray-400">
              {{ summary.warning_stations > 0 ? t('need attention') : t('No warnings') }}
            </p>
          </div>
        </div>

        <!-- Latest Warning or Monitored Types -->
        <div class="mt-auto pt-3 border-t border-gray-100">
          <template v-if="latestWarning">
            <p class="text-xs text-gray-400 mb-1">{{ t('Latest:') }}</p>
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-gray-700 truncate max-w-[140px]">
                {{ latestWarning.station_name }}
              </span>
              <span class="text-xs text-gray-400">{{ latestWarningTime }}</span>
            </div>
          </template>
          <template v-else>
            <p class="text-xs text-gray-400 mb-1">{{ t('Monitored:') }}</p>
            <div class="flex flex-wrap gap-1">
              <span class="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">{{ t('Current') }}</span>
              <span class="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">{{ t('Voltage') }}</span>
              <span class="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded">PF</span>
            </div>
          </template>
        </div>
      </RouterLink>
    </div>

    <!-- Last Updated -->
    <div v-if="lastUpdatedText" class="flex justify-end items-center text-xs text-gray-400">
      <span class="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
      {{ t('Updated {time}', { time: lastUpdatedText }) }}
    </div>
  </div>
</template>
